/**
 * WABI Visual Size Guide
 * Product-aware artwork scaling with no external dependencies.
 */
if (!customElements.get('visual-size-guide')) {
  class VisualSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.data = JSON.parse(this.querySelector('[data-vsg-data]').textContent);
      } catch (error) {
        this.disableVisualizer('Invalid product data.', error);
        return;
      }

      this.sizes = (this.data.sizes || [])
        .map((size) => ({ ...size, ...VisualSizeGuide.parseSize(size.label) }))
        .filter((size) => Number.isFinite(size.widthInches) && Number.isFinite(size.heightInches))
        .map((size) => VisualSizeGuide.applyCollectionRules(size, this.data));

      if (!this.sizes.length) {
        this.disableVisualizer(this.data.translations?.parseError || 'Artwork sizes could not be read.');
        return;
      }

      this.modal = this.closest('modal-dialog');
      this.variantPicker = this.closest('product-form')?.querySelector('variant-picker') || null;
      this.widthByFurniture = {
        sofa: 84,
        bed: 76,
        console: 60,
        dining: 72,
        fireplace: 60,
        desk: 48,
        wall: 96
      };

      const configuredFurniture = this.dataset.defaultFurniture;
      const initialFurniture = Object.prototype.hasOwnProperty.call(this.widthByFurniture, configuredFurniture)
        ? configuredFurniture
        : 'sofa';
      const initiallySelected = this.sizes.find((size) => size.label === this.data.selectedSize)
        || this.sizes.find((size) => size.selected)
        || this.sizes.find((size) => size.available)
        || this.sizes[0];

      this.state = {
        furniture: initialFurniture,
        furnitureWidth: this.widthByFurniture[initialFurniture],
        sizeLabel: initiallySelected.label,
        framed: false
      };

      this.cacheElements();
      this.populateSizes();
      this.syncFromVariantPicker();
      this.bindEvents();
      this.render();
    }

    disconnectedCallback() {
      this.modalObserver?.disconnect();
      document.removeEventListener('on:variant:change', this.boundVariantChange);
    }

    cacheElements() {
      this.els = {
        width: this.querySelector('[data-vsg-width]'),
        sizeSelect: this.querySelector('[data-vsg-size-select]'),
        sizeSingle: this.querySelector('[data-vsg-size-single]'),
        recommendation: this.querySelector('[data-vsg-recommendation]'),
        ratio: this.querySelector('[data-vsg-ratio]'),
        shape: this.querySelector('[data-vsg-shape]'),
        pieces: this.querySelector('[data-vsg-pieces]'),
        selectedLabel: this.querySelector('[data-vsg-selected-label]'),
        apply: this.querySelector('[data-vsg-apply]'),
        sceneGroup: this.querySelector('[data-vsg-scene-group]'),
        artwork: this.querySelector('[data-vsg-artwork]'),
        artPlaceholder: this.querySelector('[data-vsg-art-placeholder]'),
        artFrame: this.querySelector('[data-vsg-art-frame]'),
        artWidthLine: this.querySelector('[data-vsg-art-width-line]'),
        artWidthLabel: this.querySelector('[data-vsg-art-width-label]'),
        artHeightLine: this.querySelector('[data-vsg-art-height-line]'),
        artHeightLabel: this.querySelector('[data-vsg-art-height-label]'),
        furnitureWidthLine: this.querySelector('[data-vsg-furniture-width-line]'),
        furnitureWidthLabel: this.querySelector('[data-vsg-furniture-width-label]'),
        caption: this.querySelector('[data-vsg-caption]')
      };
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const furnitureButton = event.target.closest('[data-vsg-furniture]');
        if (furnitureButton) {
          this.selectFurniture(furnitureButton.dataset.vsgFurniture);
          return;
        }

        if (event.target.closest('[data-vsg-width-decrease]')) {
          this.setFurnitureWidth(this.state.furnitureWidth - 1, true);
          return;
        }

        if (event.target.closest('[data-vsg-width-increase]')) {
          this.setFurnitureWidth(this.state.furnitureWidth + 1, true);
          return;
        }

        const framingButton = event.target.closest('[data-vsg-framing]');
        if (framingButton) {
          this.state.framed = framingButton.dataset.vsgFraming === 'framed';
          this.render();
          return;
        }

        if (event.target.closest('[data-vsg-apply]')) {
          this.applySize();
        }
      });

      this.els.width.addEventListener('input', () => {
        const value = Number(this.els.width.value);
        if (Number.isFinite(value) && value >= 24 && value <= 240) {
          this.state.furnitureWidth = Math.round(value);
          this.widthByFurniture[this.state.furniture] = this.state.furnitureWidth;
          this.render();
        }
      });

      this.els.width.addEventListener('change', () => {
        this.setFurnitureWidth(this.els.width.value, true);
      });
      this.els.width.addEventListener('blur', () => {
        this.setFurnitureWidth(this.els.width.value, true);
      });
      this.els.width.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.setFurnitureWidth(this.els.width.value, true);
        }
      });

      this.els.sizeSelect.addEventListener('change', () => {
        this.state.sizeLabel = this.els.sizeSelect.value;
        this.track('size_guide_size_select');
        this.render();
      });

      this.boundVariantChange = () => this.syncFromVariantPicker();
      document.addEventListener('on:variant:change', this.boundVariantChange);

      if (this.modal) {
        this.modalObserver = new MutationObserver(() => {
          if (this.modal.hasAttribute('open')) {
            this.syncFromVariantPicker();
            this.track('size_guide_open');
          }
        });
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }
    }

    populateSizes() {
      const fragment = document.createDocumentFragment();
      this.sizes.forEach((size) => {
        const option = document.createElement('option');
        option.value = size.label;
        option.disabled = !size.available;
        option.textContent = VisualSizeGuide.formatDimensions(size);
        if (!size.available) {
          option.textContent += ` — ${this.data.translations?.unavailable || 'Unavailable'}`;
        }
        fragment.appendChild(option);
      });
      this.els.sizeSelect.replaceChildren(fragment);

      if (this.sizes.length === 1) {
        this.els.sizeSelect.hidden = true;
        this.els.sizeSingle.hidden = false;
        this.els.sizeSingle.textContent = VisualSizeGuide.formatDimensions(this.sizes[0]);
      }
    }

    selectFurniture(furniture) {
      if (!Object.prototype.hasOwnProperty.call(this.widthByFurniture, furniture)) return;
      this.state.furniture = furniture;
      this.state.furnitureWidth = this.widthByFurniture[furniture];
      this.els.width.value = this.state.furnitureWidth;
      this.render();
    }

    setFurnitureWidth(value, commit) {
      let width = Number(value);
      if (!Number.isFinite(width)) width = this.widthByFurniture[this.state.furniture] || 84;
      if (commit) width = Math.min(240, Math.max(24, Math.round(width)));
      this.state.furnitureWidth = width;
      this.widthByFurniture[this.state.furniture] = width;
      this.els.width.value = width;
      this.render();
    }

    syncFromVariantPicker() {
      this.variantPicker = this.closest('product-form')?.querySelector('variant-picker') || this.variantPicker;
      if (!this.variantPicker) return;

      const sizeSelector = this.findOptionSelector(this.data.optionName);
      const selectedSize = VisualSizeGuide.getSelectedOptionValue(sizeSelector);
      if (selectedSize && this.sizes.some((size) => size.label === selectedSize)) {
        this.state.sizeLabel = selectedSize;
      }

      const framingSelector = Array.from(this.variantPicker.querySelectorAll('.option-selector'))
        .find((selector) => /frame|canvas|rolled|stretch/i.test(selector.dataset.option || ''));
      const framingValue = VisualSizeGuide.getSelectedOptionValue(framingSelector);
      if (framingValue) {
        this.state.framed = /frame/i.test(framingValue) && !/frameless|rolled|stretched canvas/i.test(framingValue);
      }

      this.render();
    }

    findOptionSelector(optionName) {
      if (!this.variantPicker) return null;
      const normalizedName = (optionName || '').trim().toLowerCase();
      return Array.from(this.variantPicker.querySelectorAll('.option-selector'))
        .find((selector) => (selector.dataset.option || '').trim().toLowerCase() === normalizedName) || null;
    }

    render() {
      if (!this.state || !this.els) return;
      const size = this.sizes.find((candidate) => candidate.label === this.state.sizeLabel) || this.sizes[0];
      this.state.sizeLabel = size.label;

      this.querySelectorAll('[data-vsg-furniture]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.vsgFurniture === this.state.furniture));
      });
      this.querySelectorAll('[data-vsg-framing]').forEach((button) => {
        const pressed = (button.dataset.vsgFraming === 'framed') === this.state.framed;
        button.setAttribute('aria-pressed', String(pressed));
      });
      this.querySelectorAll('[data-vsg-furniture-svg]').forEach((group) => {
        group.toggleAttribute('hidden', group.dataset.vsgFurnitureSvg !== this.state.furniture);
      });

      this.els.width.value = this.state.furnitureWidth;
      this.els.sizeSelect.value = size.label;
      this.els.selectedLabel.textContent = VisualSizeGuide.formatDimensions(size);

      const pieces = this.inferPieces(size);
      const shape = this.inferShape(size, pieces);
      this.els.pieces.textContent = pieces.label;
      this.els.shape.textContent = shape;

      const recommendation = this.getRecommendation(size);
      this.els.recommendation.textContent = recommendation.label;
      this.els.recommendation.parentElement.dataset.status = recommendation.status;
      this.els.ratio.textContent = recommendation.ratioText;

      const liveSizeOption = this.findLiveSizeOption(size.label);
      const available = size.available && (!liveSizeOption || !liveSizeOption.classList.contains('is-unavailable'));
      this.els.apply.disabled = !available;
      this.els.apply.setAttribute('aria-disabled', String(!available));

      this.renderScene(size, pieces.count);
      this.updateCaption(size, pieces.label);
    }

    renderScene(size, pieceCount) {
      const furnitureWidth = this.state.furnitureWidth;
      const baseFurnitureWidth = 640;
      const nominalArtworkWidth = baseFurnitureWidth * (size.widthInches / furnitureWidth);
      const artworkHeight = baseFurnitureWidth * ((size.pieceHeightInches || size.heightInches) / furnitureWidth);
      const panelWidth = baseFurnitureWidth * ((size.pieceWidthInches || size.widthInches) / furnitureWidth);
      const gap = pieceCount > 1 ? Math.min(18, nominalArtworkWidth * 0.035) : 0;
      const artworkWidth = pieceCount > 1
        ? panelWidth * pieceCount + gap * (pieceCount - 1)
        : nominalArtworkWidth;
      const scale = Math.min(1, 760 / Math.max(baseFurnitureWidth, artworkWidth), 330 / artworkHeight);
      const artLeft = 500 - artworkWidth / 2;
      const artTop = 340 - artworkHeight;
      const artBottom = 340;
      const pieceWidth = pieceCount > 1 ? panelWidth : artworkWidth;

      this.els.sceneGroup.setAttribute('transform', `translate(500 590) scale(${scale}) translate(-500 -590)`);
      this.ensureArtworkPieces(pieceCount);

      Array.from(this.els.artwork.querySelectorAll('[data-vsg-piece]')).forEach((piece, index) => {
        const x = artLeft + index * (pieceWidth + gap);
        const placeholder = piece.querySelector('[data-vsg-art-placeholder]');
        const frame = piece.querySelector('[data-vsg-art-frame]');

        [placeholder, frame].forEach((element) => {
          element.setAttribute('x', x);
          element.setAttribute('y', artTop);
          element.setAttribute('width', pieceWidth);
          element.setAttribute('height', artworkHeight);
        });
        frame.setAttribute('stroke-width', this.state.framed ? '4' : '1.5');
      });

      const tx = (value) => 500 + (value - 500) * scale;
      const ty = (value) => 590 + (value - 590) * scale;
      const artLeftScaled = tx(artLeft);
      const artRightScaled = tx(artLeft + artworkWidth);
      const artTopScaled = ty(artTop);
      const artBottomScaled = ty(artBottom);
      const widthLineY = Math.min(608, artBottomScaled + 19);
      const heightLineX = Math.max(36, artLeftScaled - 18);
      const furnitureLeft = tx(180);
      const furnitureRight = tx(820);
      const furnitureLineY = Math.min(620, ty(602) + 10);

      this.els.artWidthLine.setAttribute('d', `M${artLeftScaled} ${widthLineY}h${artRightScaled - artLeftScaled}`);
      this.els.artWidthLabel.setAttribute('x', (artLeftScaled + artRightScaled) / 2);
      this.els.artWidthLabel.setAttribute('y', Math.min(632, widthLineY + 22));
      this.els.artWidthLabel.textContent = `${VisualSizeGuide.formatNumber(size.widthInches)} in W`;

      this.els.artHeightLine.setAttribute('d', `M${heightLineX} ${artTopScaled}v${artBottomScaled - artTopScaled}`);
      const heightLabelY = (artTopScaled + artBottomScaled) / 2;
      this.els.artHeightLabel.setAttribute('x', heightLineX - 16);
      this.els.artHeightLabel.setAttribute('y', heightLabelY);
      this.els.artHeightLabel.setAttribute('transform', `rotate(-90 ${heightLineX - 16} ${heightLabelY})`);
      this.els.artHeightLabel.textContent = `${VisualSizeGuide.formatNumber(size.heightInches)} in H`;

      this.els.furnitureWidthLine.setAttribute('d', `M${furnitureLeft} ${furnitureLineY}h${furnitureRight - furnitureLeft}`);
      this.els.furnitureWidthLabel.setAttribute('x', (furnitureLeft + furnitureRight) / 2);
      this.els.furnitureWidthLabel.setAttribute('y', Math.min(646, furnitureLineY + 22));
      this.els.furnitureWidthLabel.textContent = `${VisualSizeGuide.formatNumber(furnitureWidth)} in`;
    }

    ensureArtworkPieces(count) {
      let pieces = Array.from(this.els.artwork.querySelectorAll('[data-vsg-piece]'));
      if (!pieces.length) {
        const wrapper = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        wrapper.dataset.vsgPiece = '0';
        [this.els.artPlaceholder, this.els.artFrame].forEach((element) => wrapper.appendChild(element));
        this.els.artwork.appendChild(wrapper);
        pieces = [wrapper];
      }

      while (pieces.length < count) {
        const clone = pieces[0].cloneNode(true);
        clone.dataset.vsgPiece = String(pieces.length);
        this.els.artwork.appendChild(clone);
        pieces.push(clone);
      }

      pieces.forEach((piece, index) => {
        piece.toggleAttribute('hidden', index >= count);
      });
    }

    updateCaption(size, piecesLabel) {
      const furnitureLabel = this.querySelector(`[data-vsg-furniture="${this.state.furniture}"] span`)?.textContent.trim() || '';
      const template = this.data.translations?.artworkCaption || '{size} · {pieces} · {furniture} {width} in';
      this.els.caption.textContent = VisualSizeGuide.applyTemplate(template, {
        size: VisualSizeGuide.formatDimensions(size),
        pieces: piecesLabel,
        furniture: furnitureLabel,
        width: VisualSizeGuide.formatNumber(this.state.furnitureWidth)
      });
    }

    getRecommendation(size) {
      const ratio = (size.widthInches / this.state.furnitureWidth) * 100;
      const translations = this.data.translations || {};
      if (this.state.furniture === 'wall') {
        return {
          label: translations.wallGuidance,
          status: 'neutral',
          ratioText: ''
        };
      }

      const ranges = {
        sofa: [50, 60, 75, 85],
        bed: [45, 55, 75, 90],
        console: [50, 60, 80, 95],
        dining: [45, 55, 75, 90],
        fireplace: [45, 55, 80, 90],
        desk: [45, 55, 75, 90]
      };
      const [small, light, recommended, bold] = ranges[this.state.furniture] || ranges.sofa;
      let key;
      let status;
      if (ratio < small) {
        key = 'mayFeelTooSmall';
        status = 'caution';
      } else if (ratio < light) {
        key = 'lighterLook';
        status = 'neutral';
      } else if (ratio <= recommended) {
        key = 'recommended';
        status = 'recommended';
      } else if (ratio <= bold) {
        key = 'boldLook';
        status = 'bold';
      } else {
        key = 'mayFeelTooLarge';
        status = 'caution';
      }

      return {
        label: translations[key],
        status,
        ratioText: `${Math.round(ratio)}%`
      };
    }

    inferShape(size, pieces = this.inferPieces(size)) {
      const translations = this.data.translations || {};
      const orientation = VisualSizeGuide.getCollectionOrientation(this.data);
      if (orientation === 'panoramic') return translations.panoramic;
      if (orientation === 'vertical') return translations.vertical;
      if (orientation === 'horizontal') return translations.horizontal;
      if (orientation === 'square') return translations.square;
      if (orientation === 'sets') return pieces.label;
      return '—';
    }

    inferPieces(size) {
      const translations = this.data.translations || {};
      const count = size?.pieceCount || VisualSizeGuide.getPieceCount(size?.label, this.data);
      if (count === 3) {
        return { count: 3, label: translations.triptych };
      }
      if (count === 2) {
        return { count: 2, label: translations.diptych };
      }
      return { count: 1, label: translations.single };
    }

    findLiveSizeOption(label) {
      const selector = this.findOptionSelector(this.data.optionName);
      if (!selector) return null;
      return Array.from(selector.querySelectorAll('.js-option'))
        .find((option) => (option.dataset.value || option.value) === label) || null;
    }

    applySize() {
      const size = this.sizes.find((candidate) => candidate.label === this.state.sizeLabel);
      const liveOption = this.findLiveSizeOption(this.state.sizeLabel);
      if (!size || !size.available || !liveOption || liveOption.classList.contains('is-unavailable')) return;

      this.track('size_guide_apply');
      this.modal?.close();

      const customSelect = liveOption.closest('custom-select');
      if (customSelect && typeof customSelect.selectOption === 'function') {
        customSelect.selectOption(liveOption);
        return;
      }

      if (liveOption.matches('input[type="radio"]')) {
        if (!liveOption.checked) {
          liveOption.checked = true;
          liveOption.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            cancelable: false,
            detail: { noFocus: true }
          }));
        }
      }
    }

    track(eventName) {
      if (!Array.isArray(window.dataLayer)) return;
      const size = this.sizes.find((candidate) => candidate.label === this.state.sizeLabel);
      const event = { event: eventName };
      if (eventName !== 'size_guide_open' && size) {
        event.artwork_size = `${VisualSizeGuide.formatNumber(size.widthInches)}x${VisualSizeGuide.formatNumber(size.heightInches)}`;
        event.furniture = this.state.furniture;
        event.furniture_width = this.state.furnitureWidth;
      }
      window.dataLayer.push(event);
    }

    disableVisualizer(message, error) {
      this.hidden = true;
      const productId = this.dataset.productId;
      document.querySelectorAll(`open-or-scroll-to[data-target-id="visual-size-guide-${CSS.escape(productId)}"]`)
        .forEach((opener) => { opener.hidden = true; });
      if (!this.hasLoggedError) {
        console.warn('[WABI Visual Size Guide]', message, error || '');
        this.hasLoggedError = true;
      }
    }

    static getSelectedOptionValue(selector) {
      if (!selector) return '';
      const selectedDropdownOption = selector.querySelector('.custom-select__option[aria-selected="true"]');
      if (selectedDropdownOption) return selectedDropdownOption.dataset.value || '';
      const selectedRadio = selector.querySelector('.js-option:checked');
      return selectedRadio?.value || '';
    }

    static parseSize(label) {
      const source = String(label || '')
        .replace(/[″“”]/g, '"')
        .replace(/[′‘’]/g, "'")
        .replace(/×/g, 'x');
      const imperialPart = source.split('/')[0];
      const isCentimeterOnly = /cm/i.test(source)
        && !/["']|\bin(?:ches)?\b|\d\s*[wh]\b/i.test(imperialPart);
      let widthInches;
      let heightInches;

      const labeledPattern = /(\d+(?:\.\d+)?)\s*(?:'{1,2}|"|in(?:ches)?)?\s*([wh])(?=\s*(?:x|\/|\(|$))/gi;
      Array.from(imperialPart.matchAll(labeledPattern)).forEach((match) => {
        if (match[2].toLowerCase() === 'w') widthInches = Number(match[1]);
        if (match[2].toLowerCase() === 'h') heightInches = Number(match[1]);
      });

      if ((!Number.isFinite(widthInches) || !Number.isFinite(heightInches)) && !isCentimeterOnly) {
        const pair = imperialPart.match(/(\d+(?:\.\d+)?)\s*(?:'{1,2}|"|in(?:ches)?)?\s*x\s*(\d+(?:\.\d+)?)\s*(?:'{1,2}|"|in(?:ches)?)?/i);
        if (pair) {
          widthInches = Number(pair[1]);
          heightInches = Number(pair[2]);
        }
      }

      if (!Number.isFinite(widthInches) || !Number.isFinite(heightInches)) {
        const cmPair = source.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/i);
        if (cmPair) {
          widthInches = Number(cmPair[1]) / 2.54;
          heightInches = Number(cmPair[2]) / 2.54;
        }
      }

      if (!(widthInches > 0) || !(heightInches > 0)) return {};
      return { widthInches, heightInches };
    }

    static normalizeOrientation(size, data = {}) {
      const orientation = VisualSizeGuide.getCollectionOrientation(data);
      const shouldSwap = ((orientation === 'horizontal' || orientation === 'panoramic')
        && size.widthInches < size.heightInches)
        || (orientation === 'vertical' && size.widthInches > size.heightInches);
      if (!shouldSwap) return size;
      return { ...size, widthInches: size.heightInches, heightInches: size.widthInches };
    }

    static getCollectionOrientation(data = {}) {
      const orientation = String(data.orientation || '').toLowerCase();
      return ['square', 'horizontal', 'vertical', 'panoramic', 'sets'].includes(orientation)
        ? orientation
        : '';
    }

    static getPieceCount(label, data = {}) {
      if (!data.isMultiPanel) return 1;
      const source = String(label || '').toLowerCase();
      if (/\b3\s*p\b|\bset\s*of\s*3\b|\b3[- ]piece\b/.test(source)) return 3;
      if (/\b2\s*p\b|\bset\s*of\s*2\b|\b2[- ]piece\b/.test(source)) return 2;
      return 2;
    }

    static applyCollectionRules(size, data = {}) {
      const pieceCount = VisualSizeGuide.getPieceCount(size.label, data);
      if (pieceCount === 1) {
        return { ...VisualSizeGuide.normalizeOrientation(size, data), pieceCount };
      }

      const eachMatch = String(size.label || '').match(/\beach\b\s*:?-?\s*([^)]*)/i);
      const eachSize = eachMatch ? VisualSizeGuide.parseSize(eachMatch[1]) : {};
      const panelSize = {
        label: size.label,
        widthInches: Number.isFinite(eachSize.widthInches) ? eachSize.widthInches : size.widthInches,
        heightInches: Number.isFinite(eachSize.heightInches) ? eachSize.heightInches : size.heightInches
      };
      const orientedPanel = VisualSizeGuide.normalizeOrientation(panelSize, data);

      return {
        ...size,
        pieceCount,
        pieceWidthInches: orientedPanel.widthInches,
        pieceHeightInches: orientedPanel.heightInches,
        widthInches: orientedPanel.widthInches * pieceCount,
        heightInches: orientedPanel.heightInches
      };
    }

    static formatNumber(value) {
      return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10);
    }

    static formatDimensions(size) {
      return `${VisualSizeGuide.formatNumber(size.widthInches)} × ${VisualSizeGuide.formatNumber(size.heightInches)} in`;
    }

    static applyTemplate(template, values) {
      return Object.entries(values).reduce((result, [key, value]) => {
        const sentinel = `__VSG_${key.toUpperCase()}__`;
        return result.split(sentinel).join(value).split(`{${key}}`).join(value);
      }, template);
    }
  }

  customElements.define('visual-size-guide', VisualSizeGuide);
}
